import { MotionActivityIcon, SmartHomeIcon } from '@/Components/Common/Icons';
import { WellnessIcon } from '@/Components/Common/Icons/WellnessIcon';
import { DEVICE_ALERTS_EVENTS } from '@/constants/device';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Skeleton } from 'antd';
import './AlertsCard.less';

const formatAlertTime = (utc, timeZone, locale, is24hours, trans) => {
  const date = new Date(utc);

  const getDateParts = (inputDate) => {
    const parts = new Intl.DateTimeFormat(locale, {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(inputDate);

    const dateObject = {};
    parts.forEach(({ type, value }) => {
      if (type !== 'literal') {
        dateObject[type] = value;
      }
    });

    return `${dateObject.year}-${dateObject.month}-${dateObject.day}`;
  };

  const now = new Date();
  const today = getDateParts(now);

  const yesterdayDate = new Date(now);
  yesterdayDate.setDate(now.getDate() - 1);
  const yesterday = getDateParts(yesterdayDate);

  const inputDay = getDateParts(date);
  const formattedTime = new Intl.DateTimeFormat(locale, {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: !is24hours,
  }).format(date);

  if (inputDay === today) {
    return `${trans('TODAY')} ${formattedTime}`;
  }

  if (inputDay === yesterday) {
    return `${trans('YESTERDAY')} ${formattedTime}`;
  }

  return new Intl.DateTimeFormat(locale, {
    timeZone,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: !is24hours,
  }).format(date);
};

const formatAlerts = (data, is24hours, timezone, locale, trans) => {
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }

  return data.map((item) => ({
    type: item.message_type,
    time: formatAlertTime(item.utc_timestamp, timezone, locale, is24hours, trans),
    description: item.display_text,
    location: item.location,
  }));
};

const AlertsCard = ({ alerts, is24hours, timezone, locale, isFetchingAlerts }) => {
  const trans = useSelector(translator);
  const cardAlerts = useMemo(
    () => formatAlerts(alerts, is24hours, timezone, locale, trans),
    [alerts, is24hours, timezone, locale, trans]
  );

  const getAlertIcon = (type) => {
    switch (type) {
      case DEVICE_ALERTS_EVENTS.INSIGHTS_MOTION_ALERT_EVENT:
        return <MotionActivityIcon className="alert-icon" />;
      case DEVICE_ALERTS_EVENTS.INSIGHTS_SMART_HOME_ALERT_EVENT:
        return <SmartHomeIcon className="alert-icon" />;
      case DEVICE_ALERTS_EVENTS.INSIGHTS_BIOMETRICS_ALERT_EVENT:
        return <WellnessIcon className="alert-icon" />;
    }
  };

  const getAlertTitle = (type) => {
    switch (type) {
      case DEVICE_ALERTS_EVENTS.INSIGHTS_MOTION_ALERT_EVENT:
        return trans('MOTION');
      case DEVICE_ALERTS_EVENTS.INSIGHTS_SMART_HOME_ALERT_EVENT:
        return trans('SMART_HOME');
      case DEVICE_ALERTS_EVENTS.INSIGHTS_BIOMETRICS_ALERT_EVENT:
        return trans('BIOMETRICS');
    }
  };

  return (
    <>
      <div className="alert-container">
        {cardAlerts.map((alert, index) => (
          <div key={index} className="alert-card">
            <div className="alert-details">
              <div className="alert-header">
                {getAlertIcon(alert.type)}
                <span className="alert-title">{getAlertTitle(alert.type)}</span>
              </div>
              <span className="alert-time">{alert.time}</span>
              <span className="alert-description">{alert?.description}</span>
            </div>
          </div>
        ))}
        {isFetchingAlerts && (
          <div className="alert-card skeleton-card">
            <div className="alert-details">
              <div className="alert-header">
                <Skeleton.Avatar active size={24} shape="circle" />
                <Skeleton.Input active size="small" className="skeleton-title" />
              </div>
              <Skeleton.Input active size="large" className="skeleton-time" />
              <Skeleton.Input active size="small" className="skeleton-description" />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

AlertsCard.propTypes = {
  alerts: PropTypes.array,
  is24hours: PropTypes.bool,
  timezone: PropTypes.string,
  locale: PropTypes.string,
  isFetchingAlerts: PropTypes.bool,
};

export default AlertsCard;
